import Vue from "vue";
import Vuex from "vuex";
import axios from "axios";

Vue.use(Vuex);

const apiUrl = process.env.VUE_APP_API_SERVER;

// these are fields that are required to be part of a valid mapping
// in the system. The key is the field key that must be added and
// the value is the default value that should be added.
export const requiredFields = {
         offerId: "",
         sourceId: "",
         languageId: "",
         confirmationPref: "NONE",
         originatingType: "CHQ",
       };

// Probably good to take a step back here and explain what all this mess is related to FB
// data because it can seem a bit confusing. When setting up a mapping to FB, we pull the
// questions of the form from the graph api. It returns an array of questions that have
// four fields in them:
// 1. key - this is the key passed with the value when a lead is generated
// 2. label - A friendly name of the field
// 3. type - an indicator of what the field is: FIRST_NAME, STREET_ADDRESS, COUNTRY, etc.
// 4. id - some sort of database identifier for this field
// Unfortunately, the key, which is sent over in the lead with the value captured for this
// field is not necessarily the same value as the type, and further, it may be in a
// completely different language. When we initially load up the questions from FB and
// setup the editable data in the JSON editor, we create a mapping based off of the
// type but use the key. So we keep a list of what the keys are actually for behind
// the scenes. It is a set of mappings FB has indicated and we just hold on to them
// so we know how to pull things back out again on the lead (remember that the lead
// contains the key and not the type). Because this tool is primarily used by English
// speakers, we show the mappings in English. So for example, if the key sent over
// is nombre (first name in Spanish) and the type is indicated as FIRST_NAME, then we
// put in first_name but behind the scenes setup a mapping that says: "whenever you
// see nombre, treat it as first_name."
// Now, adding to the mix we have here, it was originally thought that whenever the
// type of a field was FIRST_NAME, the key would always be first_name regardless of
// language. Unfortunately we didn't have any non-English forms to test this on when
// everything was initially setup so this set of mappings (FBPredefinedFields) are
// mappings for English based forms. It proved to not be true but the lead processing
// code was already dependent on these field names so this was left as-is to help
// facilitate mapping non-English versions of fields correctly to the type they actually
// are when processing leads. Maybe at some point we'll get a chance to untangle this
// and figure out a better way forward, but because the mappings live in JSON that
// can be hand-edited by non-programmers, and the assumption is that it is English
// based, it may be sticky to ever get things to a simpler state.
// So all of that said, this set of predefined fields indicates the value that is
// shown in the editor for specific types from FB. I think I talked myself in a circle
// here and if you have any questions about these pieces please reach out so we can
// make sure you're understanding how all the pieces fit together. It is easy to get
// turned around in these mappings that happen at different times.
export const FBPredefinedFields = {
  FIRST_NAME: "first_name",
  LAST_NAME: "last_name",
  FULL_NAME: "full_name",
  EMAIL: "email",
  COUNTRY: "country",
  STREET_ADDRESS: "street_address",
  CITY: "city",
  PROVINCE: "state",
  STATE: "state",
  ZIP: "zip_code",
  POST_CODE: "post_code",
  PHONE: "phone_number",
};

export default new Vuex.Store({
  state: {
    apiUrl,
    user: null,
    authGroups: [],
    fbPages: [],
    fbPagesLoaded: false,
    fbFormsLoading: false,
    fbForms: [],
    fieldsLoading: false,
    currFbPage: null,
    currFbForm: null,
    formName: "",
    isProduction: false,
    sendToRegularEndpoint: true,
    jsonData: "{}",
    fbMappedKeys: "{}",
    isErrorJsonData: false,
    lookupData: {},
    snapForms: [],
  },
  getters: {
    isAdminUser: (state) => {
      return state.authGroups.indexOf("Administrators") > -1;
    },
  },
  mutations: {
    setUser(state, user) {
      state.user = user;
      if (user && "signInUserSession" in user) {
        Vue.set(state, "authGroups", user.signInUserSession.accessToken.payload["cognito:groups"]);
      } else {
        Vue.set(state, "authGroups", []);
      }
    },
    // for now just takes a raw object of the form selected. Should
    // not be called directly. Use the setWorkingForm action instead
    // to ensure related forms are loaded
    setWorkingForm(state, form) {
      state.currFbForm = form;
      this.commit("clearFormData");
    },
    // for now just takes a raw object of the page selected. Should
    // not be called directly. Use the setWorkingPage action instead
    // to ensure related forms are loaded
    setWorkingPage(state, page) {
      state.currFbPage = page;
      this.commit("clearForms");
    },
    // The history of this is covered more in-depth above but ths is where
    // some of the mapping work happens.
    // Sets up the jsonData based on the fields passed in, as well as the
    // FB mappings for those fields. When FB sends over data from a lead
    // it only contains the raw key and value without any of the indicators
    // such as what the type the field being passed is (or the unique id).
    // In foreign languages this becomes problematic because the key is whatever
    // was written for the label of the field. In the fields passed in there
    // are two properties on each field that we can use to remap correctly and
    // keep everything in English (for sending the data over to ICS): the
    // key (which is the label for the field) and the type (which is the
    // function or usage intended for the field, such as first name or address).
    // A separate mapping of those field types will be setup (called fbMappedKeys)
    // that is then saved with the form when created. When the webhook is
    // called for the form, then the key can be used with the fbMappedKeys
    // to translate the data from FB keys to ICS keys as setup in the jsonData
    setJsonDataFromFB(state, fields) {
      const jsonMappedKeys = {};
      const fbMappedKeys = {};
      // starting out here we're going to setup the key value pairs that appear
      // in the JSON as well as create the set of mappings between the field type
      // and the field key so we map it correctly into the data sent to ICS.
      fields.questions.forEach((field) => {
        // see if the type of this field is a predefined field
        if (field.type in FBPredefinedFields) {
          // yes it is a predefined field, going to setup the key and the value to be the
          // predefined field while also storing in our hidden mappings the relationship
          // between the field key provided by FB and the predefined field name.
          jsonMappedKeys[FBPredefinedFields[field.type]] = `{{${FBPredefinedFields[field.type]}}}`;
          fbMappedKeys[FBPredefinedFields[field.type]] = field.key;
        } else {
          // not a predefined field, meaning it is custom, so just use the key on both sides
          jsonMappedKeys[field.key] = `{{${field.key}}}`;
        }
      });
      // need to put in the checkboxes values as well for mapping. These are usually
      // used for determining email subscriptions. Checkboxes are an oddity from a
      // FB point of view in that they live in a different set of data than the questions
      // do so we have to do this extra step rather than just doing it within the questions
      // above.
      fields.checkboxes.forEach((field) => {
        jsonMappedKeys[field.key] = `{{${field.key}}}`;
      });

      // going to add on the auto add fields that aren't already mapped in
      for (const key in requiredFields) {
        jsonMappedKeys[key] = requiredFields[key];
      }
      // this gets spit out as a JSON string to the state so that it will work properly
      // with the editor.
      state.jsonData = JSON.stringify(jsonMappedKeys);
      // saving the hidden mappings
      state.fbMappedKeys = JSON.stringify(fbMappedKeys);
    },
    // this is much simpler than the world of FB. At least in the current version of Google's
    // data format we don't have to worry about multi-language support in the keys (yay!) so
    // the mappings are just one to one.
    // As the setJsonDataFromFB() this is just setting up the JSON that will be displayed
    // and editable in the JSON editor.
    setJsonDataFromWebhook(state, testData) {
      const jsonMappedKeys = {};
      for (const key in testData) {
        jsonMappedKeys[key] = `{{${key}}}`;
      }

      for (const key in requiredFields) {
        jsonMappedKeys[key] = requiredFields[key];
      }
      // this gets spit out as a JSON string to the state so that it will work properly
      // with the editor, otherwise we'd just leave it as-is.
      state.jsonData = JSON.stringify(jsonMappedKeys);
    },
    // When setting up a new form the user has to go through the process
    // of selecting the FB page the form is from first. On the add new form
    // page the FB pages the user has authorized are stored here after they
    // are loaded from the server. They are cached out in the browser for
    // performance benefits (but I'm not 100% certain it is worth it yet).
    setFbPages(state, pages) {
      state.fbPages = pages;
      state.fbPagesLoaded = true;
      this.commit("clearForms");
    },
    // After a user has chosen the FB page that the form is on, the forms
    // for that FB page are loaded up and set here. This is also with the
    // idea that it will make things faster and reduce load/calls on the
    // server but I'm not so sure it is is worth it yet.
    setFbForms(state, forms) {
      state.fbForms = forms;
      this.commit("clearWorkingForm");
    },
    // this and the other clear* functions work together to
    // clear out data as things change. Can call different
    // clear* mutators and all sub data will also automatically
    // be cleared.
    clearForms(state) {
      state.fbForms = [];
      this.commit("clearWorkingForm");
    },
    clearWorkingForm(state) {
      state.currFbForm = null;
      this.commit("clearFormData");
    },
    clearFormData(state) {
      state.jsonData = "{}";
      state.fbMappedKeys = "{}";
      state.isErrorJsonData = false;
      state.isProduction = false;
      state.sendToRegularEndpoint = true;
    },
    loadingFormsStatus(state, status) {
      state.fbFormsLoading = status;
    },
    loadingFieldsStatus(state, status) {
      state.fieldsLoading = status;
    },
    setFormName(state, name) {
      state.formName = name;
    },
    // convenience method for setting all of the fields of a form in one place
    setFormData(state, data) {
      state.jsonData = data.jsonData;
      state.fbMappedKeys = data.fbMappedKeys;
      state.isErrorJsonData = data.isError;
      state.isProduction = data.isProduction;
      state.sendToRegularEndpoint = data.sendToRegularEndpoint;
    },
    setIsProduction(state, data) {
      state.isProduction = data;
    },
    setSendToRegularEndpoint(state, data) {
      state.sendToRegularEndpoint = data;
    },
    setMappingsJson(state, mapping) {
      state.jsonData = mapping.jsonData;
      state.isErrorJsonData = mapping.isError;
    },
    setJsonErrorStatus(state, isError) {
      state.isErrorJsonData = isError;
    },
    setLookupData(state, data) {
      state.lookupData[data.dataType] = data.data;
    },
    setSnapForms(state, data) {
      state.snapForms = data;
      console.log("aaa", state.snapForms);
    },
  },
  actions: {
    // default attempt at loading of FB page info to use for building a new connection to a form
    // currently only called when the AddForm is mounted. Has a safety valve so it doesn't get
    // called multiple times per session.
    loadSavedFbPages({ state, commit }) {
      commit("clearForms");

      // ensure it only tries to load this once per browser session (or until refresh)
      if (!state.fbPagesLoaded) {
        axios({
          method: "get",
          url: apiUrl + "/pages",
          withCredentials: true,
          headers: {
            Authorization: state.user.signInUserSession.idToken.jwtToken,
          },
          params: {
            action: "GET_USER_FBPAGES",
          },
        }).then(
          (rslt) => {
            if (rslt.data.status == "ok") {
              commit("setFbPages", rslt.data.data);
            } else {
              alert("Error loading pages from server. Please refresh your browser and try again.");
            }
          },
          (err) => {
            alert(
              `Server responded with:\n\n"${err.response.data.errorMessage}"\n\nPlease refresh your browser and try again.`
            );
          }
        );
      }
    },
    // calling this saveFbPages instead of setFbPages because it also pushes the pages up to the
    // server as well as setting them in the store
    saveFbPages({ state, commit }, data) {
      axios({
        method: "post",
        url: apiUrl + "/pages",
        data: data.pages,
        headers: {
          Authorization: state.user.signInUserSession.idToken.jwtToken,
        },
        params: {
          action: "SAVE_USER_FBPAGES",
        },
        withCredentials: true,
      });
      if (data.setPages) {
        commit("setFbPages", data.pages);
      }
    },
    clearFbPages({ commit }) {
      commit("setFbPages", []);
    },
    // has the side effect of loading up the forms for the selected page
    setWorkingPage({ commit, state }, page) {
      commit("loadingFormsStatus", true);
      commit("setWorkingPage", page);
      axios({
        method: "get",
        url: apiUrl + "/forms",
        params: {
          pageId: page.id,
          action: "GET_FB_FORMS",
        },
        headers: {
          Authorization: state.user.signInUserSession.idToken.jwtToken,
        },
      }).then(
        (rslt) => {
          if (rslt.data.status == "ok") {
            commit("setFbForms", rslt.data.data);
          } else {
            alert("Problem loading up forms for page. Please refresh pages from Facebook and try again.");
          }
          commit("loadingFormsStatus", false);
        },
        (err) => {
          alert(
            `Server responded with:\n\n"${err.response.data.errorMessage}"\n\nPlease refresh your browser and try again.`
          );
          commit("loadingFormsStatus", false);
        }
      );
    },
    // has the side effect of loading up the questions for the selected form
    setWorkingForm({ commit, state }, form) {
      commit("loadingFieldsStatus", true);
      commit("setWorkingForm", form);
      axios({
        method: "get",
        url: apiUrl + "/forms",
        params: {
          pageId: state.currFbPage.id,
          formId: form.id,
          action: "GET_FORM_QUESTIONS",
        },
        headers: {
          Authorization: state.user.signInUserSession.idToken.jwtToken,
        },
      }).then(
        (rslt) => {
          if (rslt.data.status == "ok") {
            commit("setJsonDataFromFB", rslt.data.data);
          } else {
            alert("Problem loading up forms. Please refresh pages from Facebook and try again.");
          }
          commit("loadingFieldsStatus", false);
        },
        (err) => {
          alert(
            `Server responded with:\n\n"${err.response.data.errorMessage}"\n\nPlease refresh your browser and try again.`
          );
          commit("loadingFieldsStatus", false);
        }
      );
    },
    getLookupTable({ commit, state }, dataType) {
      if (state.lookupData[dataType] != null) {
        return state.lookupData[dataType];
      }

      return axios({
        method: "post",
        url: apiUrl + "/lookup-data",
        headers: {
          Authorization: state.user.signInUserSession.idToken.jwtToken,
        },
        params: { dataType, action: "LOOKUP_PER_DATATYPE" },
      })
        .then(
          (rslt) => {
            let data = rslt.data.data.map((entry) => ({
              key: entry.DataKey,
              data: JSON.parse(entry.FieldData),
            }));
            commit("setLookupData", { dataType, data });
            return data;
          },
          (err) => {
            alert(
              `Server responded with:\n\n"${err.response.data.errorMessage}"\n\nPlease refresh your browser and try again.`
            );
          }
        )
        .catch((error) => {
          console.log(error);
        });
    },
  },
});
